import { memo, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Typography } from "@mui/material";
import AppButton from "../../components/AppButton";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";

import EditorContainer from "./EditorContainer";
import {
  ContentsType,
  EditorErrorsType,
  FormErrorsType,
  LanguagesType,
} from "../interfaces";

const Editors = memo(
  ({
    activeLanguages,
    contentsFilledErrors,
    editorsErrors,
    onContentsChange,
  }: {
    activeLanguages: LanguagesType[];
    contentsFilledErrors: FormErrorsType["content"];
    editorsErrors: EditorErrorsType;
    onContentsChange: (content: ContentsType) => void;
  }) => {
    const intl = useIntl();
    const [collapseAll, setCollapseAll] = useState(false);

    const errorsFilledByLang = useMemo(() => {
      if (!contentsFilledErrors) return {};

      return contentsFilledErrors.reduce<Record<string, string>>(
        (acc, error) => {
          acc[error.id] = error.value;
          return acc;
        },
        {}
      );
    }, [contentsFilledErrors]);

    return (
      <Box
        sx={{
          width: "70%",
          maxWidth: "calc(70% - 16px)",
          gap: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "1.25rem" }}>
            {intl.formatMessage({ id: "CREATE_BANNER" })}
          </Typography>
          <AppButton
            intent="tonal"
            onClick={() => setCollapseAll((prev) => !prev)}
            sx={{
              width: "fit-content",
              alignSelf: "end",
              gap: 0.5,
              pt: 0.5,
              pr: 0.5,
              pb: 0.5,
              pl: 1.25,
            }}
          >
            {collapseAll ? (
              <>
                <FormattedMessage id="COLLAPSE_ALL" />
                <UnfoldLessIcon />
              </>
            ) : (
              <>
                <FormattedMessage id="EXPAND_ALL" />
                <UnfoldMoreIcon />
              </>
            )}
          </AppButton>
        </Box>
        {activeLanguages.map((lang: LanguagesType) => (
          <EditorContainer
            key={lang.id}
            collapseAll={collapseAll}
            errorFilledMsg={errorsFilledByLang[lang.id]}
            editorErrors={editorsErrors.errors[lang.id]}
            lang={lang}
            onContentsChange={onContentsChange}
          />
        ))}
      </Box>
    );
  }
);

export default Editors;
